import { cn } from "../../utils/tailwindUtils";

/** Shared control styling for Input, Textarea, Select, and native pickers. */
export const FIELD_CONTROL_BASE =
  "flex w-full rounded-lg border border-input bg-background text-sm text-foreground " +
  "placeholder:text-muted-foreground focus-visible:outline-none focus:ring-1 focus:ring-sidebar " +
  "focus:border-sidebar-primary disabled:cursor-not-allowed disabled:text-muted-foreground disabled:opacity-60 " +
  "transition-colors duration-200";

export const fieldControlClass = (className, { size = "default" } = {}) =>
  cn(
    FIELD_CONTROL_BASE,
    size === "default" && "h-10 px-3 py-2",
    size === "sm" && "h-9 px-3 py-1.5 text-sm",
    size === "compact" && "h-8 px-2 py-1 text-xs",
    className,
  );

/** Ant Design Select — match Input/fieldControlClass background and border. */
export const antSelectClass = (className) =>
  cn(
    "w-full",
    "[&_.ant-select-selector]:!rounded-lg [&_.ant-select-selector]:!border-input",
    "[&_.ant-select-selector]:!bg-background [&_.ant-select-selector]:!text-foreground",
    "[&_.ant-select-selector]:!shadow-none [&_.ant-select-selector]:!min-h-10",
    "[&_.ant-select-selection-placeholder]:!text-muted-foreground",
    "[&.ant-select-disabled_.ant-select-selector]:!bg-background [&.ant-select-disabled_.ant-select-selector]:!text-muted-foreground [&.ant-select-disabled]:!opacity-60",
    className,
  );

/** Ant Design DatePicker / TimePicker — className is applied on the .ant-picker root. */
export const antPickerClass = (className) =>
  cn(
    "w-full !h-10 !rounded-lg !border-input !bg-background !text-foreground !shadow-none",
    "[&_.ant-picker-input>input]:!bg-transparent [&_.ant-picker-input>input]:!text-foreground",
    "[&_.ant-picker-input>input]:!placeholder:text-muted-foreground",
    "[&.ant-picker-disabled]:!bg-background [&.ant-picker-disabled]:!opacity-60",
    "[&.ant-picker-disabled_.ant-picker-input>input]:!text-muted-foreground",
    className,
  );

/** Toolbar filters — lock Ant picker/select to the same 40px row as fieldControlClass. */
export const toolbarPickerClass = (className) =>
  cn(
    antPickerClass(className),
    "!flex !h-10 !items-center !py-0",
    "[&_.ant-picker-input]:!flex [&_.ant-picker-input]:!h-full [&_.ant-picker-input]:!items-center",
    "[&_.ant-picker-input>input]:!text-sm [&_.ant-picker-input>input]:!leading-none",
    "[&_.ant-picker-range-separator]:!flex [&_.ant-picker-range-separator]:!items-center [&_.ant-picker-range-separator]:!px-1",
    "[&_.ant-picker-suffix]:!mt-0 [&_.ant-picker-suffix]:!flex [&_.ant-picker-suffix]:!items-center",
  );

export const toolbarSelectClass = (className) =>
  cn(
    antSelectClass(className),
    "[&_.ant-select-selector]:!h-10 [&_.ant-select-selector]:!min-h-0 [&_.ant-select-selector]:!max-h-10",
    "[&_.ant-select-selector]:!flex [&_.ant-select-selector]:!items-center [&_.ant-select-selector]:!py-0",
    "[&_.ant-select-selection-item]:!leading-none [&_.ant-select-selection-placeholder]:!leading-none",
    "[&_.ant-select-selection-search]:!flex [&_.ant-select-selection-search]:!items-center",
    "[&_.ant-select-selection-search-input]:!h-full",
  );

export const toolbarInputClass = (className) =>
  fieldControlClass(cn("!h-10", className));

export const textareaControlClass = (className) =>
  cn(FIELD_CONTROL_BASE, "min-h-[5rem] px-3 py-2 h-auto", className);

/** Modal form label (matches Employee Type modals). */
export const modalFieldLabelClass =
  "text-xs font-medium text-gray-700 dark:text-gray-300";

/** Modal text/number input styling. */
export const modalInputClass =
  "w-full rounded-lg transition-colors duration-200 " +
  "border-gray-300 dark:border-gray-600 " +
  "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20";

/** Modal description textarea styling (matches Employee Type). */
export const modalTextareaClass =
  "w-full h-24 rounded-md transition-colors duration-200 " +
  "border-gray-300 dark:border-gray-600 " +
  "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20";

/** Standard description field props for CRUD modals. */
export const modalDescriptionFieldProps = {
  rows: 3,
  showCharCount: true,
  maxLength: 250,
};
